'use client';

import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import classNames from 'classnames';
import { Button } from '@get-mosh/msh-shared';
import styles from './section-with-columns.module.scss';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { ImageField } from '@prismicio/client';

/**
 * @typedef {import("@prismicio/client").Content.UspWithThreeColumnsSlice} UspWithThreeColumnsSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<UspWithThreeColumnsSlice>} UspWithThreeColumnsProps
 * @param { UspWithThreeColumnsProps }
 */
const SectionWithColumns = ({ slice, context }) => {
    const getImageSource = (image: ImageField) => {
        return {
            src: image.url || '',
            width: image.dimensions?.width,
            height: image.dimensions?.height,
            alt: image.alt || '',
        };
    };
    return (
        <section
            className={classNames(
                'py-8 xl:py-16',
                slice.variation === 'howItWorks' && styles['how-it-works']
            )}
            style={{ backgroundColor: slice.primary.background || 'bg-white' }}
        >
            <div className="container">
                <h2 className="text-center">{slice.primary.title}</h2>
                {slice.primary.description && (
                    <div className="description text-center mb-4 block">
                        <PrismicRichText field={slice.primary.description} />
                    </div>
                )}
                <div
                    className={classNames(
                        styles['columns'],
                        'py-2 md:py-8 grid grid-cols-1 md:gap-y-0 md:gap-x-8 last:pb-0',
                        slice.primary.columns
                            ? `md:grid-cols-${slice.primary.columns}`
                            : 'md:grid-cols-3'
                    )}
                >
                    {slice?.items?.map((item, i) => (
                        <div className={classNames(styles['column'])} key={i}>
                            {slice.variation === 'howItWorks' &&
                                i !== slice.items.length - 1 && <Arrow />}
                            {item.image?.url && (
                                <div className="flex justify-center">
                                    <img
                                        {...getImageSource(item.image)}
                                        className={classNames(
                                            'mb-4',
                                            styles[item.useimage]
                                        )}
                                    />
                                </div>
                            )}
                            <h3 className="mb-2 md:mb-4">{item.heading}</h3>
                            <PrismicRichText field={item.content} />
                        </div>
                    ))}
                </div>
                {(slice.primary.text_before_cta &&
                    slice.primary.text_before_cta[0]?.text) ||
                slice.primary.cta_label ||
                slice.primary.cta_link ||
                (slice.primary.text_after_cta &&
                    slice.primary.text_after_cta[0]?.text) ? (
                    <div className="cta block text-center">
                        <PrismicRichText
                            field={slice.primary.text_before_cta}
                        />
                        {slice.primary.cta_label && slice.primary.cta_link && (
                            <Button
                                text={slice.primary.cta_label}
                                href={slice.primary.cta_link}
                                as="a"
                                onClick={() => {
                                    trackClick(EventNames.BUTTON_CLICKED, {
                                        pageType: context.pageType,
                                        vertical: slice.primary.vertical,
                                        label: slice.primary.cta_label,
                                        link: slice.primary.cta_link,
                                        variation: slice.variation,
                                        location: slice.slice_type,
                                    });
                                }}
                            />
                        )}
                        <PrismicRichText field={slice.primary.text_after_cta} />
                    </div>
                ) : null}
            </div>
        </section>
    );
};

const Arrow = () => (
    <svg
        className={styles['arrow']}
        viewBox="0 0 80 74"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g opacity="0.8">
            <path
                d="M63.1563 23.4007L63.3321 23.3444L63.451 23.2432L63.6145 23.5887L63.7659 23.46L63.9145 23.2403C63.8803 23.3016 63.8547 23.3673 63.8383 23.4356C63.8383 23.4356 63.9476 23.4545 63.9027 23.5113L63.7039 23.9786L63.759 24.0435L63.8347 23.9791L64.0688 23.575C64.1181 23.6388 64.1502 23.7142 64.1619 23.794C64.1574 23.8814 64.1437 23.9681 64.1209 24.0526L64.1761 24.1175L64.295 24.0163L64.4393 23.7258C64.4393 23.7258 64.3955 23.8935 64.4123 23.9351C64.4291 23.9767 64.5871 24.1406 64.6066 24.2731C64.6261 24.4056 65.4023 24.7703 65.2873 25.0732L65.3629 25.0088L65.6657 24.7513C65.6468 24.8606 65.668 24.973 65.609 25.0791C65.5501 25.1851 65.8476 25.1185 65.7141 25.3997L65.833 25.2986C65.8271 25.3804 65.807 25.4605 65.7736 25.5355C65.6061 25.8643 65.0958 26.4475 64.9498 26.758C64.6813 27.3406 64.1537 28.013 63.8034 28.6091L63.6331 28.8472L63.7342 28.5935L63.0883 29.6088L63.0018 29.6824L63.7605 28.1424L63.6961 28.0667L63.6204 28.1311C63.533 28.3119 63.4314 28.4855 63.3167 28.6504C63.1545 28.7884 62.9707 28.9447 62.8637 29.1476C62.6092 29.4917 62.3828 29.8477 62.1483 30.1938L62.5215 29.6437L62.5539 29.6161L62.1567 30.4199C61.8318 30.8267 61.5253 31.2551 61.182 31.6403L60.0668 32.9804L58.825 34.3907L58.8899 34.3355L58.9116 34.3171L58.225 35.0875L58.2358 35.0783C57.3613 36.1287 56.4134 37.1156 55.3991 38.0317C55.1779 38.2199 54.9488 38.4148 54.6843 38.5705C54.6562 38.6415 54.6192 38.7088 54.5741 38.7708L53.9145 39.3319L51.7033 41.3991C50.8598 42.1166 49.932 42.7567 49.041 43.4401C48 44.2691 46.9157 45.0422 45.7925 45.756L42.4964 47.6652C42.1584 47.8595 41.7653 47.9889 41.4273 48.1832C41.0894 48.3774 40.8018 48.6966 40.4222 48.9077L37.9964 49.8902L37.6941 50.0168C37.6076 50.0904 37.4475 50.0775 37.3243 50.1078L37.6281 49.9611L37.7471 49.86C37.6875 49.8626 37.6287 49.8749 37.573 49.8963L36.5072 50.3742C36.2281 50.4625 35.9751 50.6032 35.6977 50.6714C35.1745 50.8001 34.6446 50.9014 34.1221 51.0102L34.138 51.0289L34.0732 51.0841L31.8073 51.7069C31.7952 51.7337 31.7773 51.7576 31.7549 51.7767C31.7324 51.7957 31.706 51.8096 31.6776 51.8172L28.0034 52.2961L27.9385 52.3513C26.4827 52.546 24.9986 52.4665 23.5325 52.5395C21.1262 52.5954 18.7245 52.3032 16.4019 51.6718C15.8916 51.5883 15.3862 51.4769 14.888 51.3382C13.6716 50.9378 12.7704 50.6234 11.8569 50.3382C10.9433 50.053 10.1276 49.5541 8.93598 49.0953C8.34421 48.8159 7.77892 48.4581 7.2104 48.1403C6.62043 47.8322 6.05636 47.4768 5.52373 47.0777C5.1629 46.8068 4.61277 46.5107 4.10428 46.1977C3.6305 45.8874 3.23049 45.4769 2.93242 44.9953C2.93242 44.9953 2.62939 44.5076 2.58501 44.4335C2.55853 44.4131 2.53661 44.3873 2.52063 44.3579C2.7062 44.2298 2.9257 44.1599 3.15116 44.157C3.18061 44.0208 3.25028 43.8966 3.35113 43.8005C3.54799 43.5491 3.7725 43.3206 4.02043 43.1194L4.79898 42.4571L6.01006 41.427C6.04649 41.4072 6.08818 41.3994 6.1293 41.4045C6.17042 41.4096 6.2089 41.4275 6.23938 41.4555L6.73607 42.0394L7.0028 42.353L7.0212 42.3747C7.18676 42.5693 7.40639 42.7179 7.5952 42.8742C8.10679 43.2695 8.65575 43.614 9.23427 43.9028C10.0716 44.3833 10.5325 44.6622 11.3142 45.2087C11.3683 45.1627 11.576 45.2096 11.5376 45.1864C12.2602 45.4663 12.1661 45.509 12.2337 45.5447C13.2025 45.9414 14.1998 46.2643 15.2173 46.5106C16.3405 46.8587 17.4862 47.1295 18.6464 47.3212C19.9234 47.5955 20.972 47.5794 22.2679 47.7444C23.2961 47.8576 24.376 47.7031 25.4631 47.7103C26.5239 47.6873 27.5809 47.5738 28.6225 47.3711C29.305 47.2752 30.0042 47.2208 30.7343 47.1589C31.4644 47.097 32.1274 46.8685 32.8071 46.6817L34.7939 46.1472C35.332 45.9691 35.8273 45.6968 36.3486 45.4771L37.5068 44.9764C38.1855 44.6787 38.9101 44.4351 39.5427 44.0833C39.7373 43.9177 39.932 43.7522 40.171 43.6607L40.8377 43.2613C40.9292 43.2033 41.0276 43.1571 41.1308 43.1239C41.1769 43.111 41.2251 43.1081 41.2725 43.1151L41.359 43.0416L41.8672 42.6093C42.224 42.3057 42.6653 42.0795 43.0773 41.8409C43.4893 41.6022 43.5477 41.6271 43.7624 41.4631L47.5687 38.5981C48.0905 38.2197 48.5821 37.8014 49.0393 37.3472L50.5099 36.0962L51.5361 35.1116L52.0551 34.6701C52.4174 34.363 52.7573 34.0303 53.072 33.6746C54.6355 31.8974 56.2757 30.1667 57.8348 28.3187L59.0327 26.7034L59.9337 25.3964C60.1645 25.1603 60.3828 24.9124 60.5879 24.6536L60.9133 24.1159L61.4565 23.3743L62.0262 22.5542L62.1344 22.4622L62.2993 22.4151L62.5109 22.6638C62.612 22.7827 62.4542 22.9915 62.4689 23.1841L62.4884 23.3166L62.6019 23.0337L62.8527 22.6712C62.8984 22.706 62.9361 22.7503 62.963 22.801C63.0902 22.9724 63.1233 23.1865 63.1563 23.4007Z"
                fill="black"
            />
            <path
                d="M21.6649 47.9778L21.097 47.9017L21.2555 47.9347L21.6649 47.9778Z"
                fill="white"
            />
            <path
                d="M34.9054 50.8794L34.6437 50.8784L34.7086 50.8232L35.3467 50.6532L34.9054 50.8794Z"
                fill="white"
            />
            <path
                d="M27.103 49.7258L25.9279 49.8123L25.0225 49.7996C24.2956 49.8215 23.5564 49.8726 22.7943 49.8312L20.5991 49.7044C20.5699 49.6799 20.545 49.6506 20.5255 49.6179L20.6672 49.6092C21.954 49.7782 23.2528 49.8392 24.5498 49.7917C25.8605 49.805 27.1697 49.7017 28.462 49.4831L28.6129 49.4852L28.5156 49.568L27.1198 49.7675L27.103 49.7258Z"
                fill="white"
            />
            <path
                d="M31.995 48.8822L33.8894 48.3704L33.9651 48.306L33.9002 48.3612L31.716 48.9704L31.5419 49.0067L31.6716 48.8963L31.9507 48.8081L31.995 48.8822Z"
                fill="white"
            />
            <path
                d="M30.6187 49.2141L29.6388 49.3767L31.2996 49.1381L30.6063 49.2433L30.6187 49.2141Z"
                fill="white"
            />
            <path
                d="M46.7912 41.8687L46.1641 42.4022L47.1059 41.7129L46.9221 41.8692L46.8464 41.9336L46.7912 41.8687Z"
                fill="white"
            />
            <path
                d="M36.5732 47.5598L35.8129 47.8711L37.3118 47.267L36.5715 47.5799L36.5732 47.5598Z"
                fill="white"
            />
            <path
                d="M51.5901 37.9357L51.0819 38.368L52.1172 37.3942L52.0631 37.4402L51.5117 37.9093L51.5901 37.9357Z"
                fill="white"
            />
            <path
                d="M40.6033 45.7905C40.492 45.8596 40.376 45.9209 40.2562 45.9739L40.1481 46.0659L40.2562 45.9739L41.5031 45.2488L41.568 45.1936L41.5031 45.2488L40.6033 45.7905Z"
                fill="white"
            />
            <path
                d="M47.4167 41.3552L48.2829 40.7303C48.2545 40.7623 48.2217 40.7902 48.1856 40.8131L47.4524 41.2876L47.2145 41.49L47.4167 41.3552Z"
                fill="white"
            />
            <path
                d="M44.5521 43.5311L43.8346 43.9364L45.0723 43.2004L45.1263 43.1544L45.0723 43.2004L44.5413 43.5403L44.5521 43.5311Z"
                fill="white"
            />
            <path
                d="M34.4766 48.206L35.4538 47.9525L35.5078 47.9066L34.6567 48.2205L34.4101 48.2812C34.4549 48.2244 34.5274 48.2 34.4766 48.206Z"
                fill="white"
            />
            <path
                d="M42.9214 44.3962L43.3967 44.1224L43.5373 44.0028L43.3751 44.1408L42.5094 44.6349L42.923 44.3762L42.9214 44.3962Z"
                fill="white"
            />
            <path
                d="M9.53488 46.6665L9.18921 46.4573L8.97766 46.2086L9.27572 46.3837L9.54457 46.5464L9.68254 46.7086L9.53488 46.6665Z"
                fill="white"
            />
            <path
                d="M54.7491 34.8572L55.0303 34.6181C54.9998 34.6548 54.9632 34.6859 54.9221 34.7101L54.5572 35.1136L54.7491 34.8572Z"
                fill="white"
            />
            <path
                d="M53.3614 36.5781L52.898 36.9536L53.3014 36.5733L53.3338 36.5457L53.5825 36.3341L53.3614 36.5781Z"
                fill="white"
            />
            <path
                d="M38.1168 47.7751C36.0416 48.6271 33.8983 49.303 31.7098 49.7958L31.6125 49.8786C31.4726 49.9162 31.329 49.9383 31.1842 49.9447C31.333 49.9223 31.4802 49.8905 31.6249 49.8494L34.7171 48.971C36.2115 48.5375 37.6699 47.9885 39.0793 47.3291C39.6366 47.0787 40.1811 46.8205 40.7129 46.5546C40.8534 46.467 41.001 46.3914 41.1541 46.3284L41.1 46.3744C40.0978 46.8719 39.0703 47.3167 38.0216 47.707L38.1168 47.7751Z"
                fill="white"
            />
            <path
                d="M41.5036 46.3664L42.3157 45.7874C42.3608 45.7386 42.4116 45.6953 42.4671 45.6587L42.2292 45.861L42.3373 45.769L42.5963 45.6792L42.4125 45.8355L41.5512 46.4005L41.4971 46.4465L41.5036 46.3664Z"
                fill="white"
            />
            <path
                d="M26.9886 50.6432L24.2645 50.7053L24.2185 50.6512L24.2726 50.6053L27.1508 50.5052L27.3834 50.4938C27.3079 50.5231 27.2284 50.5404 27.1476 50.5453L26.9702 50.6216L26.9886 50.6432Z"
                fill="white"
            />
            <path
                d="M44.6779 44.4676L44.8293 44.3388L44.7212 44.4308L44.3535 44.7435L44.2778 44.8079C44.243 44.7756 44.2121 44.7393 44.1858 44.6998L44.6271 44.4735L44.573 44.5195L44.7812 44.4356L44.6779 44.4676Z"
                fill="white"
            />
            <path
                d="M43.3041 45.3938L43.1743 45.5042L42.8391 45.7893L43.1635 45.5134L43.1094 45.5594L42.9689 45.6789L43.1419 45.5318L43.8221 45.2141L43.3041 45.3938Z"
                fill="white"
            />
            <path
                d="M29.782 50.2242L30.9485 49.9961C30.9485 49.9961 31.0036 50.061 31.1085 50.0091L30.9144 50.0437L29.8928 50.2231C29.8928 50.2231 29.8929 50.2231 29.8388 50.2691L29.782 50.2242Z"
                fill="white"
            />
            <path
                d="M39.4102 47.3456L38.744 47.6141L39.5059 47.2829L39.4102 47.3456Z"
                fill="white"
            />
            <path
                d="M28.0888 50.4903L28.0023 50.5639L27.6572 50.5964L28.0963 50.5211L28.3397 50.5005L28.6139 50.4722L28.2873 50.5264L28.0888 50.4903Z"
                fill="white"
            />
            <path
                d="M32.255 49.7791L31.8975 49.8409L31.6617 49.8923L31.8672 49.7176L32.3171 49.633L32.255 49.7791Z"
                fill="white"
            />
            <path
                d="M52.7548 38.6037L52.5385 38.7877C52.6015 38.7165 52.6744 38.6545 52.7548 38.6037L52.917 38.4658L52.7548 38.6037Z"
                fill="white"
            />
            <path
                d="M67.8309 18.1386C68.0224 18.3518 68.1448 18.618 68.1822 18.9021C68.1992 19.0711 68.2295 19.2386 68.2727 19.4029C68.3113 19.5291 68.3346 19.6594 68.342 19.7912C68.3365 20.1658 68.3844 20.5392 68.4841 20.9004C68.5882 21.6993 68.5873 22.5084 68.4812 23.307C68.4136 24.0219 68.3092 24.7328 68.1683 25.4369C67.9763 26.2215 67.8314 27.0169 67.7343 27.8189C67.8286 28.1487 67.345 29.3987 67.3543 29.7822C67.3563 29.9304 67.3202 30.0765 67.2495 30.2068C67.001 30.791 67.263 31.5372 66.996 32.0998C66.7291 32.6623 66.7345 32.844 66.5638 33.2129C66.5359 33.2997 66.5022 33.3845 66.4627 33.4666C65.899 34.3375 65.7257 35.3608 65.1728 36.2226L64.8382 36.7495C64.7498 36.8523 64.6791 36.9691 64.629 37.0951C64.3691 37.7686 63.9623 38.3757 63.4382 38.8722C63.3625 38.9365 63.276 39.0101 63.222 39.0561C63.1275 39.6024 62.629 39.9146 62.3409 40.3647C62.2448 40.503 62.1366 40.6324 62.0176 40.7515C61.907 40.8252 61.8054 40.9117 61.7148 41.009C61.4046 41.5042 61.033 41.958 60.6088 42.3599L60.5223 42.4335C60.1395 42.9363 59.6526 43.3504 59.0949 43.6476C58.7549 43.8161 58.4616 44.0656 58.2407 44.3742C58.2014 44.4352 58.145 44.4831 58.0785 44.5122C57.7459 44.6916 57.4266 44.8947 57.1231 45.1199C56.976 45.0682 56.8418 44.9852 56.7298 44.8767C56.5333 44.571 56.3605 44.2506 56.213 43.9185L56.2459 43.76C56.2923 43.6808 56.321 43.5924 56.3301 43.501C56.3392 43.4097 56.3285 43.3174 56.2987 43.2305C56.2794 43.1859 56.274 43.1364 56.2832 43.0887C56.2925 43.0409 56.3159 42.997 56.3506 42.9628C56.7835 42.4418 57.169 41.8831 57.5025 41.2934C57.6564 41.0484 57.7947 40.7938 57.9165 40.5312C58.2864 39.9554 58.6269 39.361 58.9363 38.7505C59.2698 38.1127 59.5925 37.4842 59.8675 36.8216C59.9728 36.5915 60.1122 36.3787 60.281 36.1903C60.4498 36.0233 60.5854 35.8258 60.6805 35.6082L61.0959 34.4534C61.3196 33.9276 61.5526 33.4127 61.7579 32.8653C62.1015 31.9766 62.4668 31.0695 62.7628 30.1467C62.7628 30.1467 62.8169 30.1007 62.8277 30.0915C62.9006 29.2632 63.1124 28.453 63.4542 27.6949C63.6163 27.1843 63.6983 26.6673 63.8896 26.1691C64.0808 25.6709 64.1537 25.1431 64.419 24.6005C64.5765 24.1658 64.7097 23.7227 64.8182 23.2732C64.8241 23.2021 64.8399 23.1322 64.8652 23.0655C65.2127 22.3785 66.191 19.3658 66.3018 18.992C66.3523 18.9092 66.4281 18.8447 66.518 18.8081C66.602 18.7463 66.6815 18.6787 66.7559 18.6057C66.8351 18.5455 66.9109 18.4811 66.983 18.4126C67.0341 18.3365 67.0777 18.2557 67.1132 18.1713C67.1422 18.1318 67.1825 18.1021 67.2288 18.086C67.275 18.07 67.3251 18.0684 67.3723 18.0814L67.8309 18.1386Z"
                fill="black"
            />
            <path
                d="M68.465 20.2643L68.5386 20.3508C68.7766 20.5211 68.5793 20.5958 68.5112 20.691C68.6491 20.8532 68.7043 20.918 68.4767 21.2421C68.2492 21.5661 68.3525 21.5342 68.2703 21.6786C68.2108 21.7466 68.1415 21.8055 68.0649 21.8534L68.0108 21.8993C68.0108 21.8993 68.0163 22.0811 67.9714 22.1379C67.9116 22.2056 67.8424 22.2644 67.7659 22.3126C67.2058 22.64 66.6867 23.0815 66.2115 23.3554C65.2036 23.989 64.2606 24.5675 63.2484 25.1303C62.2361 25.6932 61.126 26.097 60.1202 26.5798L58.4558 27.3619L56.7763 28.0823L55.4282 28.6885L54.8984 29.1392L54.4696 29.3362L52.5867 30.0806L51.3792 30.5672C50.9428 30.7334 50.534 30.932 50.0885 31.0874C49.8305 31.1782 49.5655 31.2477 49.2962 31.2954L49.3963 31.3034C49.3644 31.3387 49.3281 31.3696 49.2881 31.3954L48.8658 31.5124L48.2196 31.7825L46.6358 32.4402C46.3922 32.5198 46.1447 32.5873 45.8944 32.6422L45.5445 32.7348C45.555 32.7564 45.5708 32.775 45.5905 32.7888L45.5689 32.8072L45.3315 32.8787L45.3206 32.8879C45.227 32.9957 45.1176 33.0887 44.9962 33.1638C44.7358 33.2681 44.4697 33.3576 44.1992 33.4318L44.0244 33.2264C43.8803 32.978 43.7497 32.7221 43.6331 32.4596C43.5599 32.2606 43.4989 32.0572 43.4506 31.8507L43.0836 30.7838L42.9272 30.6C42.9232 30.5318 42.9152 30.4639 42.9033 30.3966L42.7122 29.646C42.6809 29.5513 42.6718 29.4507 42.6856 29.3518L42.8153 29.2415L44.616 28.6416L45.1249 28.451L45.976 28.137C46.2567 28.0288 46.5449 27.9513 46.8072 27.8214C46.8797 27.8019 46.9496 27.7738 47.0154 27.7375L47.2436 27.6553L47.7092 27.5015L48.6728 27.1664C48.9732 27.0913 49.2696 27.001 49.5607 26.8956L50.1561 26.6315C50.3075 26.5027 50.5476 26.5221 50.745 26.4474L51.476 26.1237L52.7376 25.5911L54.7286 24.7548L58.1126 23.2555C58.5065 23.0811 58.8927 22.8892 59.2754 22.6909C59.9583 22.337 60.6302 21.9627 61.3153 21.6308C62.0928 21.2303 62.8147 20.8958 63.5631 20.4829C64.08 20.1923 64.7094 19.8806 65.1771 19.5759C65.3934 19.3919 65.74 19.3394 65.9839 19.1878L66.331 19.0044L66.2116 19.2364L66.2765 19.1812L66.4062 19.0709L66.6117 18.8961L66.6333 18.8777L66.6982 18.8225L66.7955 18.7397L66.9036 18.6478L67.0048 18.7667L67.0692 18.8424L67.1557 18.7688L66.8524 19.1572L66.8741 19.1388L67.0795 18.9641L67.1985 18.8629C67.2297 18.9223 67.2737 18.974 67.3272 19.0143L67.4462 18.9131L67.4678 18.8947L67.5863 18.9244L67.5322 18.9704L68.465 20.2643Z"
                fill="black"
            />
            <path
                d="M54.9375 26.534L54.4886 26.7294C54.4886 26.7294 54.367 26.7397 54.3145 26.7657L54.1631 26.8945L52.9524 27.4211L52.098 27.7751L51.8374 27.885L52.9832 27.4135L54.652 26.7023L55.0483 26.5329L54.9726 26.5973L54.9375 26.534Z"
                fill="white"
            />
            <path
                d="M59.8295 24.0687L59.6456 24.2251C60.1485 23.9837 60.6422 23.7315 61.1267 23.4685L61.3646 23.2661L60.1177 23.9913C60.0243 24.0262 59.9278 24.0521 59.8295 24.0687Z"
                fill="white"
            />
            <path
                d="M60.6649 23.8238L60.0988 24.1003L60.6681 23.7838L60.733 23.7286L60.6649 23.8238Z"
                fill="white"
            />
            <path
                d="M62.8949 22.5237L62.1974 22.9307L62.983 22.4301L62.8857 22.5129L62.8949 22.5237Z"
                fill="white"
            />
            <path
                d="M62.1578 22.7962C62.1084 22.8474 62.0541 22.8936 61.9956 22.9342C61.8447 23.0177 61.6874 23.0892 61.5251 23.1479L62.221 22.761L62.1562 22.8162L62.1578 22.7962Z"
                fill="white"
            />
            <path
                d="M56.4656 25.942L56.842 25.771L56.6689 25.9181L56.5608 26.0101L56.3683 26.0248L56.4656 25.942Z"
                fill="white"
            />
            <path
                d="M55.6334 26.147L55.8799 26.0863L55.4528 26.2633L55.6334 26.147Z"
                fill="white"
            />
            <path
                d="M55.4707 26.4158L55.1208 26.5084L55.4031 26.3801L55.4788 26.3158L55.4707 26.4158Z"
                fill="white"
            />
            <path
                d="M59.4823 24.2521L59.266 24.4361L59.2108 24.3712L59.4839 24.2321L59.5379 24.1861L59.4839 24.2321L59.4823 24.2521Z"
                fill="white"
            />
            <path
                d="M59.4262 24.4491L59.0698 24.6217L59.5635 24.3695L59.4446 24.4707L59.4262 24.4491Z"
                fill="white"
            />
            <path
                d="M53.2748 27.2959L53.5879 27.16L53.1867 27.3895L53.2748 27.2959Z"
                fill="white"
            />
            <path
                d="M57.4496 25.1053L57.9092 24.9007L57.408 25.1221C57.408 25.1221 57.4956 25.1594 57.4496 25.1053Z"
                fill="white"
            />
            <path
                d="M49.5758 28.5785L50.0647 28.3863L49.5126 28.6136L49.5991 28.5401L49.5758 28.5785Z"
                fill="white"
            />
            <path
                d="M54.1906 26.927L54.0284 27.0649L54.3832 26.9123L54.275 27.0043L54.1906 26.927Z"
                fill="white"
            />
            <path
                d="M56.3167 25.7892L56.6931 25.6182L56.7039 25.609L56.6823 25.6274L56.2643 25.8152L56.2443 25.8136L56.2983 25.7676L56.3167 25.7892Z"
                fill="white"
            />
            <path
                d="M48.8232 28.9209L48.2787 29.1791L48.9433 28.9306L48.8232 28.9209Z"
                fill="white"
            />
            <path
                d="M61.4349 23.3929L61.131 23.5396L61.5306 23.3301L61.4441 23.4037L61.4349 23.3929Z"
                fill="white"
            />
            <path
                d="M47.4708 29.4559L47.2318 29.5474L47.1777 29.5934L47.2318 29.5474L47.7098 29.3644L47.526 29.5208L47.4708 29.4559Z"
                fill="white"
            />
            <path
                d="M55.283 26.3706L55.1289 26.4086L55.3354 26.3447L55.283 26.3706Z"
                fill="white"
            />
            <path
                d="M54.2954 26.5024L54.6918 26.333L54.2522 26.5392L54.2954 26.5024Z"
                fill="white"
            />
            <path
                d="M53.9484 26.6855L53.7402 26.7694L53.9024 26.6314L54.0106 26.5395L54.0863 26.4751L53.9998 26.5487L53.9024 26.6314L53.9484 26.6855Z"
                fill="white"
            />
            <path
                d="M55.3764 26.0861L55.5398 26.059L55.4208 26.1602L55.3764 26.0861Z"
                fill="white"
            />
            <path
                d="M50.2341 28.4101L50.4639 28.3078L50.2357 28.3901L50.2341 28.4101Z"
                fill="white"
            />
            <path
                d="M49.2255 28.8019L49.085 28.9215L49.3132 28.8392L49.2255 28.8019Z"
                fill="white"
            />
            <path
                d="M62.9204 23.0793L62.5981 23.2043L63.4963 22.6827L63.6044 22.5907C63.6044 22.5907 63.5179 22.6643 63.4746 22.7011L62.822 23.0512L62.9204 23.0793Z"
                fill="white"
            />
            <path
                d="M57.1784 25.9694L57.0919 26.043L57.1892 25.9602L58.5281 25.3432L57.8587 25.6517L57.2 25.951L57.1784 25.9694Z"
                fill="white"
            />
            <path
                d="M61.6247 23.6597L60.6293 24.2642C60.413 24.4481 60.094 24.5331 59.8225 24.6522C60.5368 24.2869 61.2435 23.8908 61.9594 23.5054L61.6783 23.7446L61.6247 23.6597Z"
                fill="white"
            />
            <path
                d="M54.7986 27.0061L54.1481 27.2053L55.2939 26.7339L55.3696 26.6695L55.2831 26.743L54.8343 26.9385L54.7986 27.0061Z"
                fill="white"
            />
            <path
                d="M54.7986 27.0061L55.7579 26.6001L54.8078 27.0169L54.7986 27.0061Z"
                fill="white"
            />
            <path
                d="M62.03 23.6321L61.5471 23.8751L61.7958 23.6636L62.2387 23.4173L62.0116 23.6105L62.03 23.6321Z"
                fill="white"
            />
            <path
                d="M58.7244 25.1579L59.4263 24.8218L59.4463 24.8235L59.4355 24.8327L58.5979 25.2283L58.5779 25.2267L58.5887 25.2175L58.7152 25.1471L58.7244 25.1579Z"
                fill="white"
            />
            <path
                d="M55.6935 26.5245L56.2781 26.2696L56.2981 26.2712L56.2873 26.2804L55.587 26.5965L55.567 26.5948L55.5778 26.5857L55.6935 26.5245Z"
                fill="white"
            />
            <path
                d="M55.4168 26.8343L55.6142 26.7596L55.7115 26.6769L55.6034 26.7688L55.4168 26.8343L55.3519 26.8895L55.4168 26.8343Z"
                fill="white"
            />
            <path
                d="M53.0099 27.7076L53.2381 27.6254L53.0099 27.7076Z"
                fill="white"
            />
            <path
                d="M56.6464 26.1986L56.8729 26.1363L56.7864 26.2099L56.6464 26.1986Z"
                fill="white"
            />
            <path
                d="M61.1259 22.8542L61.6196 22.602L60.9113 23.0182L61.1167 22.8434L61.1259 22.8542Z"
                fill="white"
            />
        </g>
    </svg>
);

export default SectionWithColumns;
